import React, { Suspense, lazy, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import useDocumentMeta from './hooks/useDocumentMeta';
import useContentProtection from './hooks/useContentProtection';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import Experience from './components/Experience';
import Projects from './components/Projects';
import Skills from './components/Skills';
import Education from './components/Education';
import Contact from './components/Contact';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import RouteChangeTracker from './components/RouteChangeTracker';
import FirstVisitLoader from './components/FirstVisitLoader';
import CustomCursor from './components/CustomCursor';

const ProjectDetail = lazy(() => import('./components/ProjectDetail'));

const HomePage = () => (
  <>
    <Hero />
    <About />
    <Experience />
    <Projects />
    <Skills />
    <Education />
    <Contact />
  </>
);

const PageLoader = () => {
  const { t } = useTranslation();

  return (
    <div
      className="min-h-screen bg-primary flex items-center justify-center"
      role="status"
      aria-label={t('common.loading')}
    >
      <div className="w-10 h-10 border-2 border-secondary border-t-transparent rounded-full animate-spin" />
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  );
};

function App() {
  useDocumentMeta();
  useContentProtection();
  const [showFirstVisitLoader, setShowFirstVisitLoader] = useState(true);

  const handleFirstVisitComplete = () => {
    setShowFirstVisitLoader(false);
  };

  return (
    <>
      {showFirstVisitLoader && <FirstVisitLoader onComplete={handleFirstVisitComplete} />}
      <CustomCursor />

      <div className="min-h-screen bg-primary overflow-x-clip w-full max-w-full">
        <Router>
          <RouteChangeTracker />
          <Routes>
            <Route
              path="/"
              element={
                <>
                  <Header />
                  <main className="overflow-x-clip w-full max-w-full">
                    <HomePage />
                  </main>
                  <Footer />
                  <ScrollToTop />
                </>
              }
            />
            <Route
              path="/project/:id"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProjectDetail />
                </Suspense>
              }
            />
          </Routes>
        </Router>
      </div>
    </>
  );
}

export default App;
